/**
 * Crewmate — Among Us style SVG avatar
 * Props:
 *   color    — fill color (hex)
 *   size     — number, px (default 48)
 *   animate  — "bounce" | "wiggle" | false
 *   crown    — show gold crown on top
 *   dead     — show ghost (crossed out visor)
 */
export default function Crewmate({ color = "#ff3cac", size = 48, animate, crown = false, dead = false }) {
  // Visor reflection colour: lighter semi-transparent white
  const visorBase = "#b8ecff";
  const visorShine = "rgba(255,255,255,0.55)";
  const shadow = "rgba(0,0,0,0.35)";
  const bodyDark = shadeColor(color, -30);

  const cls = animate === "bounce" ? "crew-bounce"
            : animate === "wiggle" ? "crew-wiggle"
            : "";

  return (
    <svg
      width={size}
      height={size * 1.25}
      viewBox="0 0 40 50"
      xmlns="http://www.w3.org/2000/svg"
      className={cls}
      style={{ display: "inline-block", filter: "drop-shadow(2px 3px 0 rgba(0,0,0,0.5))" }}
    >
      {/* Crown */}
      {crown && (
        <g transform="translate(10, -3)">
          <polygon points="0,10 4,2 10,7 16,2 20,10" fill="#ffd700" stroke="#000" strokeWidth="1.5" strokeLinejoin="round"/>
          <circle cx="0"  cy="10" r="1.5" fill="#ff3355"/>
          <circle cx="10" cy="6"  r="1.5" fill="#00f5ff"/>
          <circle cx="20" cy="10" r="1.5" fill="#3dffa0"/>
        </g>
      )}

      {/* Backpack */}
      <rect x="6" y="22" width="7" height="9" rx="2.5" fill={bodyDark} stroke="#000" strokeWidth="2"/>

      {/* Body */}
      <ellipse cx="21" cy="33" rx="16" ry="13" fill={color} stroke="#000" strokeWidth="2.5"/>

      {/* Head */}
      <ellipse cx="21" cy="17" rx="14" ry="13" fill={color} stroke="#000" strokeWidth="2.5"/>

      {/* Legs */}
      <rect x="11" y="41" width="8"  height="9" rx="3.5" fill={color} stroke="#000" strokeWidth="2"/>
      <rect x="22" y="41" width="8"  height="9" rx="3.5" fill={color} stroke="#000" strokeWidth="2"/>

      {/* Visor */}
      <ellipse cx="24" cy="16" rx="9" ry="7" fill={visorBase} stroke="#000" strokeWidth="2"/>
      {dead ? (
        /* Ghost / dead: X on visor */
        <>
          <line x1="19" y1="11" x2="29" y2="21" stroke="#ff3355" strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="29" y1="11" x2="19" y2="21" stroke="#ff3355" strokeWidth="2.5" strokeLinecap="round"/>
        </>
      ) : (
        /* Shine on visor */
        <ellipse cx="22" cy="14" rx="5" ry="3.5" fill={visorShine}/>
      )}

      {/* Body shading */}
      <ellipse cx="26" cy="30" rx="6" ry="5" fill={shadow} style={{ mixBlendMode: "multiply" }}/>
    </svg>
  );
}

/** Darken a hex color by `amount` percent */
function shadeColor(hex, amount) {
  try {
    let h = hex.replace("#", "");
    if (h.length === 3) h = h.split("").map(c => c + c).join("");
    const num = parseInt(h, 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
    const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  } catch { return hex; }
}
